/**
 * Server-side filter vocabulary for the /Leads companies table.
 * The keys mirror the filter query params of the backend companies list
 * endpoint. Revenue values are EUR in the URL and the API; only the UI
 * converts to Mio. €.
 */

export const LEAD_FILTER_PARAM_KEYS = [
	'revenueMin',
	'revenueMax',
	'employeesMin',
	'employeesMax',
	'aiScoreMin',
	'ratingMin',
	'hasWebsite',
	'hasContacts',
	'contactChannel',
	'createdFrom',
	'createdTo',
	'customerType',
	'businessModel',
	'hiringActive',
	'hasWebsiteProfile',
	'oldestCeoAge',
	'discoverySource',
	'companyKind',
];

export const RATING_MIN_OPTIONS = [3, 3.5, 4, 4.5];

const INTEGER_KEYS = new Set(['employeesMin', 'employeesMax']);
const NUMERIC_KEYS = ['revenueMin', 'revenueMax', 'employeesMin', 'employeesMax', 'aiScoreMin', 'ratingMin'];
const BOOLEAN_KEYS = ['hasWebsite', 'hasContacts', 'hiringActive', 'hasWebsiteProfile'];
const DATE_KEYS = ['createdFrom', 'createdTo'];

export const CONTACT_CHANNEL_FILTER_OPTIONS = [
	{ value: 'email', label: 'E-Mail' },
	{ value: 'phone', label: 'Telefon' },
	{ value: 'linkedin', label: 'LinkedIn' },
];

// Filters whose values come from the targeting vocabulary (filter param → vocabulary list).
export const VOCABULARY_FILTER_LISTS = {
	customerType: 'customerTypes',
	businessModel: 'businessModels',
	oldestCeoAge: 'ageBuckets',
	discoverySource: 'discoverySources',
	companyKind: 'companyKinds',
};

/**
 * German label of a filter value: contact channels from the fixed list, everything else from the
 * targeting vocabulary; the raw value while the vocabulary is loading or does not know it.
 * @param {string} key - Filter param
 * @param {string} value
 * @param {Object} [vocabulary] - Targeting vocabulary
 * @returns {string}
 */
export const enumFilterLabel = (key, value, vocabulary) => {
	const rows = key === 'contactChannel' ? CONTACT_CHANNEL_FILTER_OPTIONS : vocabulary?.[VOCABULARY_FILTER_LISTS[key]];
	return rows?.find((option) => option.value === value)?.label ?? value;
};

// Until the vocabulary is loaded every value is kept (the backend validates it); afterwards only
// the values it offers as filters.
const isKnownVocabularyValue = (rows, value) =>
	!rows || rows.some((row) => row.value === value && row.filterable !== false);

const parseNumericParam = (key, raw) => {
	if (raw === null || raw === undefined) return undefined;
	const value = Number.parseFloat(raw);
	if (!Number.isFinite(value) || value < 0) return undefined;
	if (key === 'aiScoreMin' && value > 100) return undefined;
	if (key === 'ratingMin' && !RATING_MIN_OPTIONS.includes(value)) return undefined;
	return INTEGER_KEYS.has(key) ? Math.trunc(value) : value;
};

// Calendar dates stay 'YYYY-MM-DD' strings end to end — the backend anchors them
// to Europe/Berlin day boundaries, so converting to a Date here would only risk
// shifting the day. Round-tripping rejects overflow like 2026-02-31; the year
// floor drops the partial years a date input emits while its year is typed
// (0002, 0020, 0202) in case one outlives the control's debounce.
const MIN_FILTER_YEAR = 2000;

// Same floor as an input `min` attribute, so the control rejects what the parser
// would drop anyway.
export const MIN_FILTER_DATE = `${MIN_FILTER_YEAR}-01-01`;

const parseDateParam = (raw) => {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(raw ?? '')) return undefined;
	const parsed = new Date(`${raw}T00:00:00Z`);
	if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== raw) return undefined;
	return parsed.getUTCFullYear() < MIN_FILTER_YEAR ? undefined : raw;
};

/**
 * Reads the active table filters from URL search params, keeping only set and
 * valid values (invalid numbers, out-of-range scores etc. are dropped). Key
 * order is fixed so JSON.stringify of the result is stable.
 * @param {URLSearchParams} searchParams
 * @param {Object} [vocabulary] - Targeting vocabulary; without it, vocabulary-backed values are kept as given
 * @returns {Object} Filter object with only active keys (e.g. {revenueMin: 5000000, hasContacts: false})
 */
export const parseLeadFilters = (searchParams, vocabulary) => {
	const filters = {};
	for (const key of NUMERIC_KEYS) {
		const value = parseNumericParam(key, searchParams.get(key));
		if (value !== undefined) filters[key] = value;
	}
	for (const key of BOOLEAN_KEYS) {
		const raw = searchParams.get(key);
		if (raw === 'true' || raw === 'false') filters[key] = raw === 'true';
	}
	const contactChannel = searchParams.get('contactChannel');
	if (CONTACT_CHANNEL_FILTER_OPTIONS.some((option) => option.value === contactChannel)) {
		filters.contactChannel = contactChannel;
	}
	for (const [key, list] of Object.entries(VOCABULARY_FILTER_LISTS)) {
		const value = searchParams.get(key);
		if (value && isKnownVocabularyValue(vocabulary?.[list], value)) filters[key] = value;
	}
	for (const key of DATE_KEYS) {
		const value = parseDateParam(searchParams.get(key));
		if (value !== undefined) filters[key] = value;
	}
	// An inverted range can only ever match nothing; dropping the upper bound
	// keeps the table showing results while the user is still picking dates.
	if (
		filters.createdFrom !== undefined &&
		filters.createdTo !== undefined &&
		filters.createdFrom > filters.createdTo
	) {
		delete filters.createdTo;
	}
	return filters;
};

/**
 * @param {Object} filters - Result of parseLeadFilters
 * @returns {boolean}
 */
export const hasActiveLeadFilters = (filters) => Object.keys(filters).length > 0;

const formatMio = (eur) => (eur / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 2 });

// UTC getters on a UTC-parsed date: reading it in local time could roll a
// midnight boundary back to the previous day west of Greenwich.
const formatDate = (isoDate) => {
	const date = new Date(`${isoDate}T00:00:00Z`);
	return `${String(date.getUTCDate()).padStart(2, '0')}.${String(date.getUTCMonth() + 1).padStart(2, '0')}.${date.getUTCFullYear()}`;
};

const yesNoLabel = (subject, value) => `${subject} vorhanden: ${value ? 'Ja' : 'Nein'}`;

const PROFILE_ENUM_CHIPS = [
	{ key: 'customerType', prefix: 'Kundentyp' },
	{ key: 'businessModel', prefix: 'Geschäftsmodell' },
	{ key: 'oldestCeoAge', prefix: 'Alter ältester GF' },
	{ key: 'discoverySource', prefix: 'Quelle' },
	{ key: 'companyKind', prefix: 'Firmenart' },
];

const buildProfileFilterChips = (filters, vocabulary) => {
	const chips = PROFILE_ENUM_CHIPS.filter(({ key }) => filters[key] !== undefined).map(({ key, prefix }) => ({
		id: key,
		label: `${prefix}: ${enumFilterLabel(key, filters[key], vocabulary)}`,
		paramKeys: [key],
	}));
	if (filters.hiringActive !== undefined) {
		chips.push({
			id: 'hiringActive',
			label: `Stellt ein: ${filters.hiringActive ? 'Ja' : 'Nein'}`,
			paramKeys: ['hiringActive'],
		});
	}
	if (filters.hasWebsiteProfile !== undefined) {
		chips.push({
			id: 'hasWebsiteProfile',
			label: yesNoLabel('Website-Profil', filters.hasWebsiteProfile),
			paramKeys: ['hasWebsiteProfile'],
		});
	}
	return chips;
};

/**
 * Builds the removable chip descriptors for the active filters. A min/max pair
 * is one chip whose paramKeys removes both bounds.
 * @param {Object} filters - Result of parseLeadFilters
 * @param {Object} [vocabulary] - Targeting vocabulary for the value labels
 * @returns {Array<{id: string, label: string, paramKeys: Array<string>}>}
 */
export const buildLeadFilterChips = (filters, vocabulary) => {
	const chips = [];
	if (filters.revenueMin !== undefined || filters.revenueMax !== undefined) {
		chips.push({
			id: 'revenue',
			label: `Umsatz ${filters.revenueMin !== undefined ? formatMio(filters.revenueMin) : '0'}–${
				filters.revenueMax !== undefined ? formatMio(filters.revenueMax) : '∞'
			} Mio. €`,
			paramKeys: ['revenueMin', 'revenueMax'],
		});
	}
	if (filters.employeesMin !== undefined || filters.employeesMax !== undefined) {
		chips.push({
			id: 'employees',
			label: `Mitarbeiter ${filters.employeesMin ?? '0'}–${filters.employeesMax ?? '∞'}`,
			paramKeys: ['employeesMin', 'employeesMax'],
		});
	}
	if (filters.aiScoreMin !== undefined) {
		chips.push({ id: 'aiScore', label: `AI-Score ≥ ${filters.aiScoreMin}%`, paramKeys: ['aiScoreMin'] });
	}
	if (filters.ratingMin !== undefined) {
		chips.push({
			id: 'rating',
			label: `Bewertung ≥ ${filters.ratingMin.toLocaleString('de-DE')} ★`,
			paramKeys: ['ratingMin'],
		});
	}
	if (filters.hasWebsite !== undefined) {
		chips.push({ id: 'hasWebsite', label: yesNoLabel('Website', filters.hasWebsite), paramKeys: ['hasWebsite'] });
	}
	if (filters.hasContacts !== undefined) {
		chips.push({
			id: 'hasContacts',
			label: yesNoLabel('Kontakte', filters.hasContacts),
			paramKeys: ['hasContacts'],
		});
	}
	if (filters.contactChannel !== undefined) {
		const label = enumFilterLabel('contactChannel', filters.contactChannel);
		chips.push({ id: 'contactChannel', label: `Kontakt mit ${label}`, paramKeys: ['contactChannel'] });
	}
	chips.push(...buildProfileFilterChips(filters, vocabulary));
	if (filters.createdFrom !== undefined || filters.createdTo !== undefined) {
		const from = filters.createdFrom !== undefined ? formatDate(filters.createdFrom) : undefined;
		const to = filters.createdTo !== undefined ? formatDate(filters.createdTo) : undefined;
		let label;
		if (from && to) label = from === to ? `Erstellt am ${from}` : `Erstellt ${from}–${to}`;
		else if (from) label = `Erstellt ab ${from}`;
		else label = `Erstellt bis ${to}`;
		chips.push({ id: 'created', label, paramKeys: ['createdFrom', 'createdTo'] });
	}
	return chips;
};
